import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useCarousel } from "../../../util/carouselHooks";
import { easeInOutCubic } from "../../../util/utils";
import TitleDescLeft from "../common/TitleDescLeft";
import ResponsiveImage from "../../atoms/image/ResponsiveImage";
import {
  font,
  colors,
  lineHeight,
  viewport,
} from "../../../styles/cssinjs/helpers/variables.js";

const HubCardsWithScroll = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  let items = data.cards; // Your array
  let cards = [
    { ...items[items.length - 2], dup: true },
    { ...items[items.length - 1], dup: true },
    ...items.map((item) => ({ ...item, dup: false })),
    { ...items[0], dup: true },
    { ...items[1], dup: true },
    { ...items[2], dup: true },
  ]; // Include duplicates

  const carousalOptions = {
    arrayWithoutDups: items,
    numberOfDupsInStart: 2,
    autoPlayDuration: 2000,
    transitionDuration: 1000,
    transitionFunction: easeInOutCubic,
  };

  const { ulRef, liRefs, handlers } = useCarousel(carousalOptions);
  const { savePreviousIndex, setPreviousIndex } = handlers;

  const addToRefs = (el) => {
    if (el && !liRefs.current.includes(el)) {
      liRefs.current.push(el);
    }
  };
  useEffect(() => {
    const intervalId = setInterval(() => {
      const newIndex = (activeIndex + 1) % items.length;
      setActiveIndex(newIndex);
    }, 2000);
    return () => clearInterval(intervalId);
  }, [activeIndex, items]);

  function handleDotClick(index) {
    setActiveIndex(index);
  }

  return (
    <section className="hub-section lg:pt-20 md:mt-5 md:pt-5 bg-white">
      <TitleDescLeft className="lg:mt-4 lg:mb-5 md:mt-3 md:pt-11" data={data} />
      <ul
        className="carousal-container"
        id="cardContainer"
        ref={ulRef}
        onMouseEnter={savePreviousIndex}
        onMouseLeave={setPreviousIndex}
      >
        {cards.map((card, index) => (
          <HubCard
            key={index}
            card={card}
            index={index}
            refs={{ addToRefs }}
            handlers={handlers}
          />
        ))}
      </ul>
      <div className="dots mt-4">
        {items.map((_, index) => (
          <span
            key={index}
            className={index === activeIndex ? "active" : ""}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
      <style global jsx>{`
        .carousal-container {
          display: flex;
          flex-wrap: nowrap;
          transition: transform 3s ease-out;
          overflow: auto;
          padding: 10px 0;
          width: 100% !important;
          max-width: 100% !important;
          gap: 40px;
        }
        .carousal-container::-webkit-scrollbar {
          display: none;
        }
        .hub-section .dots {
          display: flex;
          justify-content: center;
        }
        .hub-section .dots span {
          width: 8px;
          height: 8px;
          background-color: #ccc;
          border-radius: 50%;
          margin: 0 8px;
          cursor: pointer;
        }
        .hub-section .dots span.active {
          background-color: ${colors.primitive.blue[200]};
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .carousal-container {
            gap: 16px;
          }
        }
      `}</style>
    </section>
  );
};

const HubCard = ({ index, card, refs, handlers }) => {
  const {
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd,
  } = handlers;

  return (
    <li
      key={index}
      className="carousal-item card-shadow md:d-block br-8 lg:py-3 lg:px-3 md:pt-2.5 md:pr-2.5 md:pb-2.5 md:pl-2.5 lg:mt-5 md:mt-4"
      ref={refs.addToRefs}
      onMouseEnter={() => handleMouseEnter(index)}
      onMouseLeave={handleMouseLeave}
      onTouchStart={() => handleTouchStart(index)}
      onTouchEnd={handleTouchEnd}
      aria-hidden={card.dup ? "true" : "false"}
    >
      <Link href={card.href} prefetch={false} className="carousal-item-link">
        <figure>
          <ResponsiveImage
            mobile="mobile"
            src={card.imageSrc}
            alt={card.imageAlt}
          />
        </figure>
        <article className="lg:mt-3 md:mt-2.5 ">
          <h3
            className={`ff-euclid text-black has-line text-mdl ${card.lineColor}`}
          >
            {card.title}
          </h3>
        </article>
      </Link>
      <style global jsx>{`
        .carousal-item {
          min-width: 364px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(48, 58, 71, 0.1),
            0 1px 2px rgba(48, 58, 71, 0.2);
          border-radius: 8px;
          transition: box-shadow 0.3s ease-in-out, opacity 0.3s ease-in-out;
        }
        .carousal-item {
          box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2),
            0 1px 3px 0 rgba(48, 58, 71, 0.1);
        }
        .carousal-item:hover {
          box-shadow: 0 3px 6px 0 rgba(48, 58, 71, 0.08),
            0 10px 20px 0 rgba(48, 58, 71, 0.15);
          border-color: transparent;
        }
        .has-line::after {
          margin-top: 16px;
        }
        .has-line:after {
          content: "";
          display: block;
          width: 80px;
          height: 8px;
          border-radius: 8px;
        }
        .--yellow:after {
          background-color: ${colors.primitive.yellow.default};
        }
        h3.--yellow {
          color: ${colors.primitive.yellow.default} !important;
        }
        .--orange:after {
          background-color: ${colors.primitive.red[300]};
        }
        h3.--orange {
          color: ${colors.primitive.red[300]} !important;
        }
        .--red:after {
          background-color: ${colors.primitive.red[200]};
        }
        h3.--red {
          color: ${colors.primitive.red[200]} !important;
        }
        .--green:after {
          background-color: ${colors.primitive.green[400]};
        }
        h3.--green {
          color: ${colors.primitive.green[400]} !important;
        }
        .--blue:after {
          background-color: ${colors.primitive.blue[50]};
        }
        h3.--blue {
          color: ${colors.primitive.blue[50]} !important;
        }
        .--violet:after {
          background-color: ${colors.primitive.violet[200]};
        }
        h3.--violet {
          color: ${colors.primitive.violet[200]}!important;
        }
        .--purple:after {
          background-color: ${colors.primitive.violet[300]};
        }
        h3.--purple {
          color: ${colors.primitive.violet[300]}!important;
        }
        .--grey:after {
          background-color: ${colors.primitive.grey[900]};
        }
        h3.--grey {
          color: ${colors.primitive.grey[900]}!important;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .carousal-item {
            min-width: 285px;
            min-height: 280px;
          }
        }
      `}</style>
    </li>
  );
};

export default HubCardsWithScroll;
